import { atom, selector, selectorFamily } from 'recoil'

import {
  type CritType,
  type CriterionAnagraphicField,
  type CriterionFilter,
  type CriterionGroups,
  type CriterionOptinDate,
  type CriterionOptoutMessage,
  type CriterionOptoutType,
  type SearchFiltersState,
  defaultSearchStatus,
  isCriteriaValid,
  isCriterionAnagraphicField,
} from 'Pages/RecipientsList/model'

export const searchFiltersState = atom<SearchFiltersState>({
  key: 'recipientsList_searchFiltersState',
  default: {
    contact: { value: '', dirty: false },
    status: { value: defaultSearchStatus, dirty: false },
    criteria: {
      value: [],
      dirty: false,
    },
  },
})

export const appliedSearchFiltersState = atom<SearchFiltersState>({
  key: 'recipientsList_appliedSearchFiltersState',
  default: {
    contact: { value: '', dirty: false },
    status: { value: defaultSearchStatus, dirty: false },
    criteria: {
      value: [],
      dirty: false,
    },
  },
})

export const searchFiltersEnabledSubmitState = selector<boolean>({
  key: 'recipientsList_searchFiltersEnabledSubmitState',
  get: ({ get }) => {
    const { criteria } = get(searchFiltersState)

    return isCriteriaValid(criteria.value)
  },
})

export const canResetSearch = selector<boolean>({
  key: 'recipientsList_canResetSearch',
  get: ({ get }) => {
    const { contact, criteria } = get(searchFiltersState)

    return contact.value.length > 0 || criteria.value.length > 0
  },
})

export const critTypeState = selectorFamily<CritType, number>({
  key: 'recipientsList_critTypeState',
  get:
    (index: number) =>
    ({ get }) => {
      const { criteria } = get(searchFiltersState)

      return criteria.value[index].critType
    },
})

export const identifierState = selectorFamily<
  CriterionAnagraphicField['identifier'],
  number
>({
  key: 'recipientsList_identifierState',
  get:
    (index: number) =>
    ({ get }) => {
      const { criteria } = get(searchFiltersState)

      const item = criteria.value[index]

      return isCriterionAnagraphicField(item) ? item.identifier : undefined
    },
})

export const anagraphicFieldMatchOperatorState = selectorFamily<
  CriterionAnagraphicField['matchOperator'],
  number
>({
  key: 'recipientsList_anagraphicFieldMatchOperatorState',
  get:
    (index: number) =>
    ({ get }) => {
      const { criteria } = get(searchFiltersState)

      const item = criteria.value[index]

      return isCriterionAnagraphicField(item) ? item.matchOperator : undefined
    },
})

export const criterionAnagraphicFieldState = selectorFamily<
  CriterionAnagraphicField,
  number
>({
  key: 'recipientsList_criterionAnagraphicFieldState',
  get:
    (index: number) =>
    ({ get }) => {
      const { criteria } = get(searchFiltersState)

      const item = criteria.value[index]

      return item as CriterionAnagraphicField
    },
})

export const criterionGroupsState = selectorFamily<CriterionGroups, number>({
  key: 'recipientsList_criterionGroupsState',
  get:
    (index: number) =>
    ({ get }) => {
      const { criteria } = get(searchFiltersState)

      const item = criteria.value[index]

      return item as CriterionGroups
    },
})

export const criterionOptinDateState = selectorFamily<
  CriterionOptinDate,
  number
>({
  key: 'recipientsList_criterionOptinDateState',
  get:
    (index: number) =>
    ({ get }) => {
      const { criteria } = get(searchFiltersState)

      const item = criteria.value[index]

      return item as CriterionOptinDate
    },
})

export const criterionOptoutTypeState = selectorFamily<
  CriterionOptoutType,
  number
>({
  key: 'recipientsList_criterionOptoutTypeState',
  get:
    (index: number) =>
    ({ get }) => {
      const { criteria } = get(searchFiltersState)

      const item = criteria.value[index]

      return item as CriterionOptoutType
    },
})

export const criterionOptoutMessageState = selectorFamily<
  CriterionOptoutMessage,
  number
>({
  key: 'recipientsList_criterionOptoutMessageState',
  get:
    (index: number) =>
    ({ get }) => {
      const { criteria } = get(searchFiltersState)

      const item = criteria.value[index]

      return item as CriterionOptoutMessage
    },
})

export const criterionFilterState = selectorFamily<CriterionFilter, number>({
  key: 'recipientsList_criterionFilterState',
  get:
    (index: number) =>
    ({ get }) => {
      const { criteria } = get(searchFiltersState)

      const item = criteria.value[index]

      return item as CriterionFilter
    },
})

export const searchGroupsInState = selector<number[]>({
  key: 'recipientsList_searchGroupsInState',
  get: ({ get }) => {
    const search = get(appliedSearchFiltersState)

    const groupsInCrit = search.criteria.value.filter(
      (c) => c.critType === 'groups' && c.matchOperator === 'BelongsTo',
    )

    const groupsIn = groupsInCrit
      .reduce((acc, curr: CriterionGroups) => [...acc, ...curr.values], [])
      .filter((g) => g !== undefined && g > 0)

    return Array.from(new Set(groupsIn))
  },
})
